import React, { useCallback, useRef, useState } from 'react';
import { View, StyleSheet, FlatList, Text, ListRenderItem, TouchableOpacity, ScrollView, Platform } from 'react-native';
import { showConfirm } from '@/utils/common/alert';
import { useNavigation } from '@react-navigation/native';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { SearchBar, InlineEmptyMessage, LoadingSpinner, AvatarCircle } from '@/components';
import { ADMIN_COLORS, EMPTY_MESSAGES } from '@/constants/admin';
import { getFilterEmptyState, getNoResultsState } from '@/utils/admin/messages/emptyState';
import { useConversationUtils } from '@/hooks/admin/useMessages';
import { ConversationCard } from '@/components/admin/messages/ConversationCard';
import { ChatWindow } from '@/components/admin/messages/ChatWindow';
import { AnchoredDropdown } from '@/components/common/admin/desktop/dropdown/AnchoredDropdown';
import { DesktopSidePanel } from '@/components/common/admin/desktop/panel/DesktopSidePanel';
import { DesktopPanelHeader } from '@/components/common/admin/desktop/panel/DesktopPanelHeader';
import { WebConfirmModal } from '@/components/common/modals/desktop/DesktopConfirmModal';
import { DesktopChatOptions } from '@/components/admin/messages/desktop/DesktopChatOptions';
import { ConversationDetailsPanel } from '@/components/admin/messages/desktop/ConversationDetailsPanel';
import { useConversations, useMessageFilters } from '@/hooks/admin/useMessages';
import { useGetConversationQuery } from '@/store/api/admin/messagesApi';
import { IConversation, IConversationAccount, FilterType, FILTER_VALUES } from '@/types/admin/messages';
import { useDebouncedValue } from '@/hooks/common/useDebouncedValue';
import { AnchorRect } from '@/hooks/common/useAnchoredPosition';
import { SVG_COMPONENTS } from '@/assets/admin';
import { ThreadOwnerModal } from '@/components/admin/messages/ThreadOwnerModal';
import { useArchiveConversationMutation } from '@/store/api/admin/messagesApi';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';

export const DesktopMessagesView: React.FC = () => {
  const navigation = useNavigation<any>();
  const [selectedConversation, setSelectedConversation] = useState<number | undefined>(undefined);

  // Sort dropdown state
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortButtonRef = useRef<View | null>(null);
  const [sortAnchor, setSortAnchor] = useState<AnchorRect | null>(null);

  // Chat options dropdown state
  const [isChatOptionsOpen, setIsChatOptionsOpen] = useState(false);
  const chatOptionsButtonRef = useRef<View | null>(null);
  const [chatOptionsAnchor, setChatOptionsAnchor] = useState<AnchorRect | null>(null);

  // Desktop side panel state for details
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);

  // Thread Owner modal
  const [showThreadOwnerModal, setShowThreadOwnerModal] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 250);

  // Conversations state & search
  const {
    conversations,
    isLoading,
    error,
    setConversationQuery,
    hasNextPage,
    loadMore,
    filterType,
    setFilterType,
    sortType,
    setSortType,
  } = useConversations();

  const { sortOptions } = useMessageFilters();

  const [archiveConversation, { isLoading: isArchiving }] = useArchiveConversationMutation();
  const { showSuccess } = useToast();
  const { showApiError } = useErrorToast();

  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);

  const hasData = (conversations || []).length > 0;

  const { data: conversationData } = useGetConversationQuery(
    { conversationId: selectedConversation as number },
    { skip: !selectedConversation, pollingInterval: 0, refetchOnMountOrArgChange: true }
  );
  const conversation = conversationData?.response;

  const onCreateMessage = useCallback(() => {
    navigation.navigate('CreateMessage');
  }, [navigation]);

  const onEditConversation = useCallback(() => {
    if (selectedConversation) {
      navigation.navigate('EditMessage', { conversationId: selectedConversation });
    }
  }, [navigation, selectedConversation]);

  const onSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  React.useEffect(() => {
    setConversationQuery(debouncedSearch);
  }, [debouncedSearch]); // Remove setConversationQuery from dependencies

  const renderConversationItem: ListRenderItem<IConversation> = useCallback(({ item }) => (
    <ConversationCard
      conversation={item}
      onPress={() => setSelectedConversation(item.id)}
      isSelected={selectedConversation === item.id}
    />
  ), [selectedConversation]);

  const openSort = useCallback(() => {
    if (sortButtonRef.current) {
      sortButtonRef.current.measureInWindow((x, y, width, height) => {
        const anchor: AnchorRect = { x, y, w: width, h: height };
        setSortAnchor(anchor);
        setIsSortOpen(true);
      });
    }
  }, []);

  const closeSort = useCallback(() => {
    setIsSortOpen(false);
  }, []);

  const openChatOptions = useCallback(() => {
    if (chatOptionsButtonRef.current) {
      chatOptionsButtonRef.current.measureInWindow((x, y, width, height) => {
        const anchor: AnchorRect = { x, y, w: width, h: height };
        setChatOptionsAnchor(anchor);
        setIsChatOptionsOpen(true);
      });
    }
  }, []);

  const closeChatOptions = useCallback(() => {
    setIsChatOptionsOpen(false);
  }, []);

  const handleArchive = useCallback(async () => {
    if (!conversation) return;
    if (Platform.OS === 'web') {
      setArchiveConfirmOpen(true);
      return;
    }
    // Mobile/native fallback - use cross-platform confirmation
    const confirmed = await showConfirm(
      'Archive Conversation',
      'Are you sure you want to archive this conversation? It will be moved to the archived section.',
      'Archive',
      'Cancel'
    );
    
    if (!confirmed) return;
    
    try {
      await archiveConversation(conversation.id).unwrap();
      showSuccess('Conversation archived successfully');
      closeChatOptions();
    } catch (error: any) {
      showApiError(error?.data?.message || 'Failed to archive conversation');
    }
  }, [archiveConversation, conversation, showApiError, showSuccess, closeChatOptions]);

  const renderFilterTabs = () => (
    <View style={styles.filtersRow}>
      {Object.values(FILTER_VALUES).map((label) => {
        const isSelected = FILTER_VALUES[filterType] === label;
        const onPress = () => {
          const key = (Object.keys(FILTER_VALUES) as FilterType[]).find(
            (k) => FILTER_VALUES[k] === label
          ) as FilterType;
          if (key && key !== filterType) {
            // Reset selection when switching filters
            setSelectedConversation(undefined);
            setFilterType(key);
          }
        };
        return (
          <TouchableOpacity
            key={label}
            style={[styles.filterTab, isSelected && styles.filterTabSelected]}
            onPress={onPress}
            activeOpacity={0.8}
          >
            <Text style={[styles.filterTabText, isSelected && styles.filterTabTextSelected]}>
              {label}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );

  const renderSortRow = () => (
    <View style={styles.sortRow}>
      <View style={styles.sortWrap} ref={sortButtonRef}>
        <TouchableOpacity
          style={styles.sortToggle}
          onPress={openSort}
          activeOpacity={0.8}
        >
          <Text style={styles.sortToggleText}>
            {sortOptions.find(o => o.key === sortType)?.value || 'Sort by latest'}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const { getRecipientDisplayName } = useConversationUtils();

  const renderLeftPane = () => {
    let content: React.ReactNode;

    if (isLoading && !hasData) {
      content = (
        <View style={styles.spinnerWrap}>
          <LoadingSpinner message="Loading conversations..." />
        </View>
      );
    } else if (error && !hasData) {
      content = (
        <View style={styles.errorWrap}>
          <InlineEmptyMessage
            title={'Unable to Load Conversations'}
            message={'Something went wrong while loading the conversations.'}
            icon={'X_CIRCLE' as any}
          />
        </View>
      );
    } else if (!hasData) {
      // Filter-specific empty states + search no-results
      const trimmedQuery = (searchQuery || '').trim();
      const cfg = trimmedQuery.length > 0
        ? getNoResultsState(trimmedQuery)
        : getFilterEmptyState(filterType);
      content = (
        <InlineEmptyMessage
          title={cfg.title}
          message={cfg.message}
          icon={cfg.icon}
          containerStyle={styles.emptyContainer}
          titleStyle={styles.emptyTitle}
          messageStyle={styles.emptyMessage}
        />
      );
    } else {
      content = (
        <FlatList
          data={conversations}
          keyExtractor={(item) => String(item.id)}
          renderItem={renderConversationItem}
          style={styles.list}
          contentContainerStyle={styles.listContent}
          onEndReachedThreshold={0.5}
          onEndReached={() => {
            if (hasNextPage && !isLoading) {
              loadMore();
            }
          }}
        />
      );
    }

    return (
      <View style={styles.leftContent}>
        {renderFilterTabs()}
        {renderSortRow()}
        {content}
      </View>
    );
  };

  const renderRightPane = () => {
    if (!selectedConversation) {
      const cfg = EMPTY_MESSAGES.messages.default;
      return (
        <View style={styles.placeholderWrap}>
          <InlineEmptyMessage
            title={cfg.title}
            message={cfg.message}
            icon={cfg.icon}
          />
        </View>
      );
    }

    return (
      <View style={styles.chatWrap}>
        {/* Chat Header */}
        {conversation && (
          <View style={styles.chatHeader}>
            <View style={styles.chatHeaderText}>
              <Text style={styles.chatHeaderTitle} numberOfLines={1}>
                {conversation.subject}
              </Text>
              <Text style={styles.chatHeaderSubtitle} numberOfLines={1}>
                {getRecipientDisplayName(conversation)}
              </Text>
            </View>
            <TouchableOpacity
              ref={chatOptionsButtonRef}
              style={styles.chatMenuButton}
              onPress={openChatOptions}
              activeOpacity={0.8}
            >
              <SVG_COMPONENTS.EDIT_DOT width={24} height={24} color={ADMIN_COLORS.TEXT.PRIMARY} />
            </TouchableOpacity>
          </View>
        )}

        <View style={styles.chatBody}>
          <ChatWindow conversationId={selectedConversation} />
        </View>
      </View>
    );
  };

  const participants: IConversationAccount[] = (conversation?.conversationAccounts || []).filter((acct: any) => acct.type === 'resident');
  const threadOwners: IConversationAccount[] = (conversation?.conversationAccounts || []).filter((acct: any) => acct.type === 'admin');

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <DesktopAdminLayout>
      <DesktopPageHeader title="Messages" showAddButton onAddPress={onCreateMessage} addButtonText="New Message">
        <View style={styles.headerSearch}>
          <SearchBar placeholder="Search" value={searchQuery} onChangeText={onSearchChange} />
        </View>
      </DesktopPageHeader>

      <View style={styles.row}>
        <View style={styles.leftPane}>
          {renderLeftPane()}
        </View>
        <View style={styles.rightPane}>
          {renderRightPane()}
        </View>
      </View>

      {/* Top-level dropdowns to avoid clipping by pane containers */}
      <AnchoredDropdown isOpen={isSortOpen} anchor={sortAnchor} width={sortAnchor?.w || 280} maxHeight={360} onRequestClose={closeSort}>
        <View>
          {sortOptions.map((option) => (
            <TouchableOpacity
              key={option.key}
              style={styles.sortMenuItem}
              onPress={() => {
                setSortType(option.key);
                closeSort();
              }}
            >
              <Text style={styles.sortMenuItemText}>{option.value}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </AnchoredDropdown>

      <DesktopChatOptions
        isOpen={isChatOptionsOpen}
        anchor={chatOptionsAnchor}
        onClose={closeChatOptions}
        onShowDetails={() => setShowDetailsPanel(true)}
        onEdit={onEditConversation}
        onArchive={handleArchive}
        onThreadOwner={() => setShowThreadOwnerModal(true)}
        isArchiving={isArchiving}
      />

      {/* Desktop conversation details side panel */}
      <DesktopSidePanel visible={showDetailsPanel} onClose={() => setShowDetailsPanel(false)} width={420}>
        {conversation && (
          <ConversationDetailsPanel conversation={conversation} onClose={() => setShowDetailsPanel(false)} />
        )}
      </DesktopSidePanel>

      {/* Thread Owner Modal */}
      {conversation && (
        <ThreadOwnerModal
          visible={showThreadOwnerModal}
          onClose={() => setShowThreadOwnerModal(false)}
          conversation={conversation}
        />
      )}

      {/* Web confirm modal for archiving */}
      <WebConfirmModal
        visible={archiveConfirmOpen}
        title={'Archive Conversation'}
        message={'Are you sure you want to archive this conversation? It will be moved to the archived section.'}
        confirmText={'Archive'}
        cancelText={'Cancel'}
        onConfirm={async () => {
          if (!conversation) return;
          try {
            await archiveConversation(conversation.id).unwrap();
            showSuccess('Conversation archived successfully');
            setArchiveConfirmOpen(false);
            closeChatOptions();
          } catch (error: any) {
            showApiError(error?.data?.message || 'Failed to archive conversation');
            setArchiveConfirmOpen(false);
          }
        }}
        onCancel={() => setArchiveConfirmOpen(false)}
      />
    </DesktopAdminLayout>
  );
};

const styles = StyleSheet.create({
  headerSearch: {
    minWidth: 260,
  },
  row: {
    flexDirection: 'row',
    gap: 12,
    flex: 1,
    minHeight: 360,
  },
  leftPane: {
    width: 360,
    maxWidth: 420,
    alignSelf: 'stretch',
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    borderRadius: 12,
    overflow: 'hidden',
  },
  leftContent: {
    flex: 1,
    padding: 8,
  },
  rightPane: {
    flex: 1,
    alignSelf: 'stretch',
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    borderRadius: 12,
    overflow: 'hidden',
  },
  spinnerWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 320,
    padding: 24,
  },
  errorWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 320,
    padding: 24,
  },
  list: {
    flex: 1,
  },
  listContent: {
    paddingVertical: 8,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 24,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    textAlign: 'center',
    marginBottom: 8,
  },
  emptyMessage: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 360,
  },
  placeholderWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  chatWrap: {
    flex: 1,
  },
  chatHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  chatHeaderText: {
    flex: 1,
    marginRight: 12,
  },
  chatHeaderTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 2,
  },
  chatHeaderSubtitle: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  chatMenuButton: {
    padding: 8,
  },
  chatBody: {
    flex: 1,
  },
  filtersRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
    width: '100%',
  },
  sortRow: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    marginBottom: 12,
    width: '100%',
  },
  filterTab: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    marginTop: 10,
  },
  filterTabSelected: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
  filterTabText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    fontWeight: '600',
  },
  filterTabTextSelected: {
    color: '#FFFFFF',
  },
  sortWrap: {
    position: 'relative',
    flex: 1,
  },
  sortToggle: {
    width: '100%',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
  },
  sortToggleText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    fontWeight: '500',
    textAlign: 'left',
  },
  sortMenuItem: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  sortMenuItemText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
});


